import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { ButtonBase, Icon } from '@material-ui/core';
import { setTab, setPage } from 'services/salesHistorySlice/salesHistorySlice';
import apiSlice from 'services/api';
import { useGetSalesHistoryTabsQuery } from 'services/api/sales';
import { useDeviceInfo } from 'hooks/useDeviceInfo';
import { Line } from 'components/HeaderUI';
import { ExpandedMenuItem, MenuItem as MenuIcon } from 'components/Icons/MenuIcon';
import { MobileSalesHistoryFilters } from './MobileFilters';

const TAB_LABELS: Record<string, string> = {
  all: 'All',
  sold: 'SOLD',
  waiting_on_title: 'Waiting on Title'
};

const Wrapper = styled.div`
  position: fixed;
  top: 72px;
  left: 0;
  right: 0;
  z-index: 100;
  background: #fff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  color: #225a91;
  flex-shrink: 0;
  margin-left: 12px;

  &:hover {
    opacity: 0.7;
  }
`;

const Title = styled.span`
  font-size: 24px;
  margin-left: 12px;
  font-weight: 500;
  color: #000;
  margin-right: 16px;
  white-space: nowrap;
`;

const TabsContainer = styled.div`
  display: flex;
  align-items: center;
  background: #fff;
  height: 80px;
`;

const TabButton = styled.button<{ selected: boolean }>`
  flex: 1;
  min-width: fit-content;
  background: transparent;
  color: ${({ selected }) => (selected ? '#225a91' : '#666')};
  font-size: 18px;
  font-weight: ${({ selected }) => (selected ? '500' : '400')};
  line-height: 100%;
  border: none;
  max-width: 200px;
  outline: none;
  cursor: pointer;
  padding: 21px 12px;
  border-radius: 0;
  position: relative;
  transition: color 0.2s, font-weight 0.2s, background 0.2s;

  &:hover {
    color: #225a91;
    font-weight: 500;
  }

  &::after {
    content: '';
    display: block;
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    width: 80%;
    height: 4px;
    background: ${({ selected }) => (selected ? '#3994DE' : 'transparent')};
    bottom: 4px;
    border-radius: 2px;
    transition: background 0.2s;
  }

  &:hover::after {
    background: #3994de;
  }
`;


const DEFAULT_TABS = [
  { value: 'all', count: 0 },
  { value: 'sold', count: 0 },
  { value: 'waiting_on_title', count: 0 }
];

const HeaderV2 = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { isMobile } = useDeviceInfo();
  const [expanded, setExpanded] = useState(false);
  const currentTab = useSelector((state: any) => state.salesHistory.tab);
  const { data: tabsData } = useGetSalesHistoryTabsQuery();

  const tabs = tabsData ?? DEFAULT_TABS;

  const handleRefresh = () => {
    dispatch(setPage({ tab: 'all', page: 1 }));
    dispatch(setPage({ tab: 'sold', page: 1 }));
    dispatch(setPage({ tab: 'waiting_on_title', page: 1 }));
    dispatch(apiSlice.util.invalidateTags(['Sales']));
    document.querySelector<HTMLElement>('[data-tab-panel="active"]')?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <Wrapper>
      <TabsContainer>
        <BackButton onClick={() => history.goBack()} aria-label="Go back">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M20 11H7.83L13.42 5.41L12 4L4 12L12 20L13.41 18.59L7.83 13H20V11Z" fill="#225a91" />
          </svg>
        </BackButton>
        <Title>Sales History</Title>
        {!isMobile && tabs.map(tab => (
          <TabButton key={tab.value} selected={currentTab === tab.value} onClick={() => { dispatch(setTab(tab.value)); }}>
            {TAB_LABELS[tab.value] ?? tab.value} ({tab.count})
          </TabButton>
        ))}
        {isMobile && (
          <>
            <span style={{ flex: 1 }} />
            <ButtonBase onClick={() => setExpanded(!expanded)} style={{ marginRight: 12 }}>
              {expanded ? <ExpandedMenuItem /> : <MenuIcon />}
            </ButtonBase>
          </>
        )}
        {!isMobile && <span style={{ flex: 1 }} />}
        <ButtonBase onClick={handleRefresh} aria-label="Refresh" style={{ marginRight: 12 }}>
          <Icon>refresh</Icon>
        </ButtonBase>
      </TabsContainer>
      {isMobile && expanded && <Line />}
      {isMobile && expanded && <MobileSalesHistoryFilters />}
    </Wrapper>
  );
};

export default HeaderV2;
